/**
 * dopamiles-cart-mutations.js
 * Phase 05 strangler-split: DOM-mutation helpers extracted from dopamiles-cart.js.
 *
 * Exported as window.bncCartMutations (IIFE, no bundler).
 * Loaded with defer AFTER dopamiles-cart-helpers.js, BEFORE dopamiles-cart.js.
 *
 * Depends on: window.bncCartHelpers (helpers must load first)
 *
 * Exports:
 *   swapSection(html, containerId)       — swap a child element by ID into live DOM
 *   applyCartMutation(result, ctx)       — post-mutation section swap + bubble + pubsub
 *
 * applyCartMutation takes a `ctx` arg to avoid coupling back to cart.js:
 *   ctx = { rebindSurface, sectionId, pageSectionId }
 */

(function () {
  'use strict';

  // ── Dependency guard ──────────────────────────────────────────────────────

  if (!window.bncCartHelpers) {
    console.warn('[bnc-cart-mutations] bncCartHelpers not loaded — mutations will not work');
    return;
  }

  var helpers = window.bncCartHelpers;

  // ── swapSection ───────────────────────────────────────────────────────────

  /**
   * Parse a Shopify section HTML response and swap a child element by ID
   * into the live DOM. Returns true if swap occurred.
   *
   * Moved from dopamiles-cart.js (phase 05 extraction).
   * DOMParser call replaced with helpers.parseSectionDoc.
   *
   * @param {string} html        — raw section HTML string
   * @param {string} containerId — id of element to swap
   * @returns {boolean}
   */
  function swapSection(html, containerId) {
    if (!html) return false;
    var doc = helpers.parseSectionDoc(html);
    if (!doc) return false;
    var src = doc.getElementById(containerId);
    var dst = document.getElementById(containerId);
    if (!src || !dst) return false;
    dst.innerHTML = src.innerHTML;
    return true;
  }

  // ── applyCartMutation ─────────────────────────────────────────────────────

  /**
   * Shared post-mutation routine: swap bundled sections, update header bubble,
   * publish cartUpdate, dispatch cart:change custom event.
   *
   * Moved from dopamiles-cart.js (phase 05 extraction).
   * Made pure via `ctx` arg so this module has zero back-reference to cart.js.
   *
   * Key behaviour preserved:
   *  - Drawer section: swap content wrap AND header count label separately.
   *    (#bnc-cart-count-label lives in .bnc-drawer-head OUTSIDE content wrap,
   *    so swapSection alone leaves it stale — surfaced in Phase 02 visual probe.)
   *  - Cart-page section: swap three regions (meta, lines, summary).
   *    Round-2 only swapped meta+lines; summary was stale after qty change.
   *  - rebindSurface only called for surfaces that were actually swapped.
   *    Fixes listener-leak from round-1 (bindDrawerEvents rebinding both
   *    surfaces on every event → accumulating handlers per click).
   *
   * @param {object} result — { cart, sections } returned by addToCart/changeCartItem
   * @param {object} ctx    — { rebindSurface: fn, sectionId: string, pageSectionId: string }
   */
  function applyCartMutation(result, ctx) {
    var sections      = result && result.sections ? result.sections : {};
    var cart          = result && result.cart;
    var rebindSurface = ctx && ctx.rebindSurface;
    var sectionId     = ctx && ctx.sectionId;
    var pageSectionId = ctx && ctx.pageSectionId;

    var drawerSwapped = false;
    var pageSwapped   = false;

    // ── Drawer section swap ───────────────────────────────────────────────

    if (sectionId && sections[sectionId]) {
      // Parse once, swap both content wrap AND header count label.
      var drawerDoc  = helpers.parseSectionDoc(sections[sectionId]);
      if (drawerDoc) {
        var newContent = drawerDoc.getElementById('bnc-cart-drawer-content');
        var dstContent = document.getElementById('bnc-cart-drawer-content');
        if (newContent && dstContent) {
          dstContent.innerHTML = newContent.innerHTML;
          drawerSwapped = true;
        }
        var newLabel = drawerDoc.getElementById('bnc-cart-count-label');
        var curLabel = document.getElementById('bnc-cart-count-label');
        if (newLabel && curLabel) curLabel.innerHTML = newLabel.innerHTML;
      }
    }

    // ── Cart-page section swap ────────────────────────────────────────────

    if (pageSectionId && sections[pageSectionId] && document.getElementById('bnc-cart-page')) {
      // Three swappable regions: meta (header), lines (items), summary (right rail).
      var tmpDoc = helpers.parseSectionDoc(sections[pageSectionId]);
      if (tmpDoc) {
        var ids = ['bnc-page-meta', 'bnc-page-lines', 'bnc-page-summary'];
        ids.forEach(function (id) {
          var src = tmpDoc.getElementById(id);
          var dst = document.getElementById(id);
          if (src && dst) {
            dst.innerHTML = src.innerHTML;
            pageSwapped = true;
          }
        });
      }
    }

    // ── Bubble + pubsub + custom event ────────────────────────────────────

    if (cart) {
      helpers.updateBubbleCount(helpers.displayCount(cart));
      helpers.publishCartUpdate(cart);
      // Custom event for any external listener (back-compat).
      document.dispatchEvent(new CustomEvent('cart:change', { detail: cart, bubbles: true }));
    }

    // ── Re-bind only swapped surfaces ─────────────────────────────────────

    if (typeof rebindSurface === 'function') {
      if (drawerSwapped) rebindSurface('drawer');
      if (pageSwapped)   rebindSurface('page');
    }
  }

  // ── Export ────────────────────────────────────────────────────────────────

  window.bncCartMutations = {
    swapSection: swapSection,
    applyCartMutation: applyCartMutation,
  };

})();
